import { type JSX, useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Group,
  Loader,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  type Capacidades,
  type ListaPessoas,
  api,
  explicarMotivo,
} from '../lib/api.js';
import { BarraDeFiltros, BlocoDeAjuda, FaixaDeNumeros, Numero, Vazio } from './ui.js';
import { codigoDoCurso } from '../lib/curso.js';
import { formatarCpf, formatarEmail } from '../lib/formato.js';

/**
 * Pessoas: o espelho do SAGA.
 *
 * O valor desta tela é DIAGNÓSTICO, não cadastro. A pergunta que ela responde
 * é "quantos alunos o bot consegue reconhecer, e por que os outros não" — e a
 * resposta vira a lista que a secretaria leva para corrigir no sistema
 * acadêmico. Por isso não há edição: corrigir dos dois lados cria divergência.
 */

const FILTROS_TELEFONE = [
  { value: '', label: 'Todos' },
  { value: 'com', label: 'Com celular válido' },
  { value: 'ausente', label: 'Sem telefone no SAGA' },
  { value: 'fixo', label: 'Telefone fixo' },
  { value: 'formato', label: 'Formato não reconhecido' },
  { value: 'duplicado', label: 'Número repetido' },
  { value: 'descoberto', label: 'Telefone descoberto pelo bot' },
];

/**
 * Cabeçalho de coluna agrupada.
 *
 * A `nota` diz o que está empilhado embaixo — sem ela, "Cadastro" não anuncia
 * que ali moram ANAC, CPF e nascimento, e a pessoa procura três colunas que
 * não existem mais. Quando um filtro fixa o valor da linha de baixo, a nota
 * passa a dizer QUAL é o valor fixo, e o dado some das células.
 */
/**
 * O que o filtro já diz, a tabela não repete.
 *
 * Com o filtro de Tipo em "aluno", a coluna repetiria a mesma palavra em todas
 * as linhas — informação zero ocupando largura. O cabeçalho passa a dizer
 * "todos: aluno" e a célula mostra só o que varia.
 *
 * `com` fica de fora de propósito: filtrar por "tem celular válido" não torna
 * o NÚMERO redundante, só a ausência dele é que seria.
 *
 * Exportada por ser a única decisão com lógica aqui — o resto é passar o
 * booleano adiante, e isso o compilador já garante.
 */
export function redundanciaDoFiltro(
  tipo: string,
  telefone: string,
): { tipoFixo: boolean; telefoneFixo: boolean } {
  return {
    tipoFixo: tipo !== '',
    telefoneFixo: telefone !== '' && telefone !== 'com',
  };
}

function Cabecalho({ titulo, nota }: { titulo: string; nota?: string }): JSX.Element {
  return (
    <Stack gap={0}>
      <Text size="sm" fw={600} lh={1.2}>
        {titulo}
      </Text>
      {nota && (
        <Text size="xs" c="dimmed" fw={400} lh={1.2} tt="none">
          {nota}
        </Text>
      )}
    </Stack>
  );
}

function data(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString('pt-BR') : '—';
}

function soData(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString('pt-BR') : '—';
}

export function Pessoas({ capacidades }: { capacidades: Capacidades }): JSX.Element {
  const [dados, setDados] = useState<ListaPessoas | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [busca, setBusca] = useState('');
  const [tipo, setTipo] = useState('');
  const [telefone, setTelefone] = useState('');
  const [sincronizando, setSincronizando] = useState(false);

  const carregar = useCallback(async () => {
    const p = new URLSearchParams();
    if (busca.trim()) p.set('busca', busca.trim());
    if (tipo) p.set('tipo', tipo);
    if (telefone) p.set('telefone', telefone);
    try {
      setDados(await api.get<ListaPessoas>(`/admin/pessoas?${p.toString()}`));
      setErro(null);
    } catch (e) {
      setErro(explicarMotivo(e, 'falha ao carregar as pessoas'));
    }
  }, [busca, tipo, telefone]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  async function sincronizar(): Promise<void> {
    setSincronizando(true);
    try {
      await api.post('/admin/pessoas/sincronizar', {});
      notifications.show({
        color: 'teal',
        title: 'Sincronização pedida',
        message: 'O worker executa em instantes. Recarregue daqui a pouco para ver o resultado.',
      });
    } catch (e) {
      notifications.show({
        color: 'red',
        title: 'Não foi possível pedir a sincronização',
        message: explicarMotivo(e, 'falha ao enfileirar'),
      });
    } finally {
      setSincronizando(false);
    }
  }

  if (erro && !dados) {
    return (
      <Alert color="red" variant="light" role="alert">
        <Stack gap="xs" align="flex-start">
          <Text size="sm">{erro}</Text>
          <Button size="xs" onClick={() => void carregar()}>
            Tentar de novo
          </Button>
        </Stack>
      </Alert>
    );
  }

  if (!dados) {
    return (
      <Group gap="xs">
        <Loader size="sm" />
        <Text c="dimmed">Carregando…</Text>
      </Group>
    );
  }

  const { resumo, ultimaSincronizacao: ultima } = dados;
  const semCelular = resumo.total - resumo.reconhecidos;

  const { tipoFixo, telefoneFixo } = redundanciaDoFiltro(tipo, telefone);

  return (
    <Stack gap="sm">
      {/* O estado da sincronização é o primeiro: lista velha sem aviso é pior
          que lista vazia, porque parece atual. */}
      <Alert
        color={!ultima ? 'gray' : ultima.ok ? 'teal' : 'red'}
        variant="light"
        title={
          !ultima
            ? 'Nunca sincronizou'
            : ultima.ok
              ? `Última sincronização em ${data(ultima.iniciadaEm)}`
              : `A última sincronização falhou (${data(ultima.iniciadaEm)})`
        }
      >
        <Group justify="space-between" wrap="wrap" gap="sm" align="flex-start">
          <Stack gap={2}>
            {!ultima ? (
              <Text size="sm">
                A lista vive vazia até a primeira rodada. Ligar é decisão de quem opera o
                servidor — <strong>SAGA_SYNC_ATIVO</strong> precisa estar em <code>true</code>.
              </Text>
            ) : ultima.ok ? (
              <Text size="sm">
                {ultima.recebidos} recebidos · {ultima.criados} criados ·{' '}
                {ultima.atualizados} atualizados · {ultima.desativados} desativados ·{' '}
                {ultima.comTelefone} com celular · pedida por {ultima.origem}
              </Text>
            ) : (
              <Text size="sm">{ultima.erro}</Text>
            )}
            {ultima && ultima.tiposDesconhecidos.length > 0 && (
              <Text size="sm">
                <strong>Tipo de acesso desconhecido no SAGA:</strong>{' '}
                {ultima.tiposDesconhecidos.join(', ')} — essas pessoas ficaram sem tipo e o
                menu não vai diferenciá-las.
              </Text>
            )}
          </Stack>
          {capacidades.administrar && (
            <Button size="xs" variant="filled" loading={sincronizando} onClick={() => void sincronizar()}>
              Sincronizar agora
            </Button>
          )}
        </Group>
      </Alert>

      {/* A frase que a seção existe para dizer. */}
      <FaixaDeNumeros>
        <Numero rotulo="Alunos" valor={resumo.alunos} nota="no SAGA, ativos" />
        <Numero
          rotulo="Reconhecidos pelo bot"
          valor={resumo.reconhecidos}
          nota="têm celular válido"
        />
        <Numero
          rotulo="Sem celular válido"
          valor={semCelular}
          cor={semCelular > 0 ? 'orange' : undefined}
          nota={semCelular > 0 ? 'o bot não os identifica' : null}
        />
        <Numero
          rotulo="Instrutores"
          valor={resumo.instrutores}
          nota={`+ ${resumo.administrativos} administrativo(s)`}
        />
      </FaixaDeNumeros>

      {/* A fila de correção mais barata do painel: o número já está aqui. */}
      {resumo.telefonesDescobertos > 0 && (
        <Alert color="teal" variant="light" title="Telefones descobertos pela autoidentificação">
          <Group justify="space-between" wrap="wrap" gap="sm">
            <Text size="sm">
              <strong>
                {resumo.telefonesDescobertos} pessoa(s)
              </strong>{' '}
              se identificaram pelo CANAC e o cadastro delas no SAGA está sem telefone. O número
              já está aqui — basta copiar para o SAGA, e o bot passa a reconhecê-las sozinho.
            </Text>
            <Button size="xs" onClick={() => setTelefone('descoberto')}>
              Ver a lista
            </Button>
          </Group>
        </Alert>
      )}

      <BarraDeFiltros>
        <TextInput
          size="xs"
          label="Buscar"
          placeholder="nome, apelido, e-mail ou ANAC"
          value={busca}
          onChange={(e) => setBusca(e.currentTarget.value)}
          w={240}
        />
        <Select
          size="xs"
          label="Tipo"
          w={160}
          value={tipo}
          onChange={(v) => setTipo(v ?? '')}
          data={[{ value: '', label: 'Todos' }, ...dados.tipos.map((t) => ({ value: t, label: t }))]}
        />
        <Select
          size="xs"
          label="Telefone"
          w={220}
          value={telefone}
          onChange={(v) => setTelefone(v ?? '')}
          data={FILTROS_TELEFONE}
        />
        <Button
          size="xs"
          component="a"
          href={`/admin/pessoas/csv?${new URLSearchParams({
            ...(busca.trim() ? { busca: busca.trim() } : {}),
            ...(tipo ? { tipo } : {}),
            ...(telefone ? { telefone } : {}),
          }).toString()}`}
        >
          Exportar CSV
        </Button>
      </BarraDeFiltros>

      <BlocoDeAjuda
        secoes={[
          {
            id: 'como',
            titulo: 'Para que serve esta lista',
            corpo: (
              <>
                <Text size="sm">
                  <strong>Esta tela não edita nada.</strong> A fonte é o SAGA, e corrigir dos
                  dois lados criaria divergência — o nome certo aqui e o errado lá, sem ninguém
                  saber qual vale. O que se corrige, corrige-se no SAGA; aqui se descobre{' '}
                  <em>o que</em> corrigir.
                </Text>
                <Text size="sm">
                  <strong>“Reconhecido pelo bot” quer dizer: tem celular válido no SAGA.</strong>{' '}
                  É por ele que a conversa no WhatsApp é ligada à pessoa. Sem celular, quem
                  escreve é tratado como interessado — ainda que seja aluno há anos.
                </Text>
                <Text size="sm">
                  <strong>Telefone fixo não vira celular.</strong> Número de 10 dígitos é fixo, e
                  o bot não inventa o nono dígito: o número que sairia disso pode ser de outra
                  pessoa, e aí a conversa dela seria casada com este cadastro. Filtre por{' '}
                  <em>telefone fixo</em> e peça o celular a essas pessoas.
                </Text>
                <Text size="sm">
                  <strong>Número repetido</strong> é a mesma linha em duas pessoas. Nenhuma das
                  duas fica com ele, porque o bot não teria como saber de quem é a conversa.
                </Text>
                <Text size="sm">
                  <strong>Quem sai do SAGA é desativado, nunca apagado</strong> — o histórico de
                  conversas aponta para o contato, e apagar deixaria atendimento órfão.
                </Text>
                <Text size="sm">
                  <strong>“Descoberto pelo bot”</strong> é telefone que apareceu sozinho: a
                  pessoa escreveu no WhatsApp, se identificou pelo código ANAC, e o cadastro
                  dela no SAGA estava sem número. O bot <strong>não</strong> grava esse número
                  no cadastro — a fonte continua sendo o SAGA, e a próxima sincronização
                  apagaria de volta. Copie para lá e o telefone passa a valer de verdade.
                </Text>
                <Text size="sm" c="dimmed">
                  O CPF aparece conforme o papel: inteiro para admin, mascarado para editor, e
                  para leitor ele não vem nem na resposta do servidor. O telefone segue a mesma
                  regra que já vale nas outras telas.
                </Text>
              </>
            ),
          },
        ]}
      />

      <Text size="sm" c="dimmed">
        {dados.itens.length} na tela · {dados.total} no filtro
      </Text>

      {dados.itens.length === 0 ? (
        <Vazio titulo="Nenhuma pessoa nesta lista.">
          {resumo.total === 0
            ? 'A sincronização com o SAGA ainda não trouxe ninguém. Enquanto ela não rodar, o bot trata todo mundo como interessado.'
            : 'Nenhuma pessoa neste filtro. Limpe a busca ou troque o recorte de telefone.'}
        </Vazio>
      ) : (
        <Table.ScrollContainer minWidth={720}>
          <Table verticalSpacing={10}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>
                  <Cabecalho titulo="Pessoa" nota="apelido abaixo" />
                </Table.Th>
                <Table.Th>
                  <Cabecalho
                    titulo={tipoFixo ? 'Curso' : 'Tipo e curso'}
                    nota={tipoFixo ? `todos: ${tipo}` : 'curso abaixo'}
                  />
                </Table.Th>
                <Table.Th>
                  <Cabecalho
                    titulo="Telefone"
                    nota={
                      telefoneFixo
                        ? `todos: ${FILTROS_TELEFONE.find((f) => f.value === telefone)?.label.toLowerCase() ?? telefone}`
                        : 'motivo abaixo'
                    }
                  />
                </Table.Th>
                <Table.Th>
                  <Cabecalho
                    titulo="Cadastro"
                    nota={capacidades.verCpf ? 'ANAC · CPF · nascimento' : 'ANAC · nascimento'}
                  />
                </Table.Th>
                <Table.Th>
                  <Cabecalho titulo="E-mail" />
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {dados.itens.map((p) => {
                const codigo = codigoDoCurso(p.curso);
                const secundarioDoCadastro = [
                  capacidades.verCpf ? formatarCpf(p.cpf ?? null) : null,
                  p.nascimento ? soData(p.nascimento) : null,
                ]
                  .filter(Boolean)
                  .join(' · ');

                return (
                  <Table.Tr key={p.id} opacity={p.ativo ? 1 : 0.55}>
                    {/* Pessoa: nome, apelido abaixo. "Ativo" deixou de ser
                        coluna — "sim" repetido em toda linha é ruído; o que é
                        sinal é a exceção, e ela vira selo. */}
                    <Table.Td>
                      <Stack gap={0}>
                        <Text size="sm">{p.nome ?? '—'}</Text>
                        {p.apelido && (
                          <Text size="xs" c="dimmed">
                            {p.apelido}
                          </Text>
                        )}
                        {!p.ativo && (
                          <Badge size="xs" color="gray" variant="light" mt={2}>
                            desativado
                          </Badge>
                        )}
                      </Stack>
                    </Table.Td>

                    {/* Tipo e curso. Com o filtro de tipo ativo o selo some:
                        ele repetiria a mesma palavra em toda linha, e o
                        cabeçalho já diz qual é. */}
                    <Table.Td>
                      <Stack gap={2} align="flex-start">
                        {!tipoFixo && (
                          <Badge
                            size="xs"
                            variant="light"
                            color={p.tipo === 'desconhecido' ? 'orange' : 'gray'}
                          >
                            {p.tipo}
                          </Badge>
                        )}
                        {codigo ? (
                          <Tooltip label={p.curso} disabled={!p.curso || p.curso === codigo}>
                            <Text size="xs" c="dimmed">
                              {codigo}
                            </Text>
                          </Tooltip>
                        ) : (
                          <Text size="xs" c="dimmed">
                            sem curso
                          </Text>
                        )}
                      </Stack>
                    </Table.Td>

                    {/* Telefone: o que a célula tem a dizer, sem linha vazia.

                        O travessão saiu de cima: ele ocupava a linha principal
                        para anunciar que não havia nada, enquanto a linha de
                        baixo já dizia isso melhor — e com o motivo, que é o
                        acionável. Agora o `—` só aparece quando a célula
                        FICARIA vazia, que é o caso do filtro por motivo sem
                        telefone descoberto: ali o motivo está no cabeçalho e
                        não há mais nada a mostrar. */}
                    <Table.Td>
                      <Stack gap={2} align="flex-start">
                        {p.telefone && (
                          <Text size="sm" ff="monospace">
                            {p.telefone}
                          </Text>
                        )}
                        {/* Texto, não só cor: o motivo é a informação acionável. */}
                        {!p.telefone && !telefoneFixo && (
                          <Text size="sm" c="orange">
                            {p.telefoneMotivo ?? 'sem telefone'}
                          </Text>
                        )}
                        {p.telefoneDescoberto && (
                          <Text size="xs" c="teal" ff="monospace">
                            {telefoneFixo ? p.telefoneDescoberto : `bot: ${p.telefoneDescoberto}`}
                          </Text>
                        )}
                        {!p.telefone &&
                          !p.telefoneDescoberto &&
                          (telefoneFixo || !p.telefoneMotivo) && (
                            <Text size="sm" c="dimmed">
                              —
                            </Text>
                          )}
                      </Stack>
                    </Table.Td>

                    {/* Cadastro: ANAC em cima; CPF e nascimento abaixo. O CPF
                        só existe aqui quando o papel recebe o campo — para
                        leitor ele nem vem na resposta da API. */}
                    <Table.Td>
                      <Stack gap={0}>
                        <Text size="sm" ff="monospace">
                          {p.anac ?? '—'}
                        </Text>
                        {secundarioDoCadastro && (
                          <Text size="xs" c="dimmed" ff="monospace">
                            {secundarioDoCadastro}
                          </Text>
                        )}
                      </Stack>
                    </Table.Td>

                    <Table.Td>
                      <Text size="sm">{formatarEmail(p.email) ?? '—'}</Text>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
    </Stack>
  );
}
